import { useRef, useCallback, useEffect, useState } from 'react';
import { useCurrentUser } from '../auth';
import { expandWidget, getOuterContainer, getInnerContainer, armSonax, isSonaxArmed } from '@/utils/sonaxWidgetControl';

// Flag para habilitar logs de debug do interceptor SIP. Desligar em produção.
const SONAX_DEBUG = false;

export const useSonaxWidget = (externalControl?: { canLoad: boolean }) => {
  // Nota: externalControl.canLoad não impede o carregamento do script
  // O script deve sempre carregar para manter o widget ativo
  // O controle externo pode ser usado para outras funcionalidades se necessário
  const { user, loading: userLoading } = useCurrentUser();

  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // Gate global: o script só carrega quando ARMADO (operate / click-to-call / softphone do header).
  const [armed, setArmed] = useState(isSonaxArmed());
  const scriptLoadedRef = useRef(false);
  const scriptElementRef = useRef<HTMLScriptElement | null>(null);
  const processingRef = useRef(false);
  const loadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastPathRef = useRef<string>('');
  const loadingStartTimeRef = useRef<number>(0);
  const loadingInitiatedRef = useRef(false);
  const loadAttemptsRef = useRef(0);
  const maxLoadingTime = 10000; // 10 segundos máximo para carregamento
  const maxLoadAttempts = 3; // Máximo de 3 tentativas
  
  // Ref para armazenar dados da ligação atual (identifica a ligação no fim, para deduplicar)
  const callDataRef = useRef<{
    data_inicio?: string;
    hora_inicio?: string;
    data_fim?: string;
    hora_fim?: string;
    telefone?: string;
    deal_id?: string;
  } | null>(null);

  /** Quando a ligação veio da lista de atividades (/activities), concluir essa atividade ao encerrar a chamada (PeerConnection::stop). */
  const pendingCrmActivityIdRef = useRef<string | null>(null);
  
  // Lock síncrono para prevenir execuções simultâneas do handler de fim de ligação
  const hangupLockRef = useRef(false);
  // Timestamp da última execução do handler de fim de ligação (para prevenir execuções muito próximas)
  const lastHangupExecutionRef = useRef<number>(0);
  // Set global para rastrear ligações já processadas (usando identificador único)
  // Inicializa no window para ser compartilhado entre todas as instâncias
  if (!(window as any).__processedCalls) {
    (window as any).__processedCalls = new Set<string>();
  }
  const processedCallsSet = (window as any).__processedCalls as Set<string>;

  // Sincroniza o estado local `armed` com o gate global (window.__sonaxArmed).
  useEffect(() => {
    const onArmChange = () => setArmed(isSonaxArmed());
    window.addEventListener('sonax:arm-changed', onArmChange);
    return () => window.removeEventListener('sonax:arm-changed', onArmChange);
  }, []);

  const hasScript = Boolean(user?.sonax_widget_script?.trim());

  // Verifica se a página atual permite usar o widget Sonax
  const isAllowedPage = useCallback(() => {
    const currentPath = window.location.pathname;
    // Permite em qualquer página autenticada (exceto auth e root)
    return currentPath !== '/auth' && currentPath !== '/';
  }, []);

  // Função para injetar o script do Sonax no DOM
  const loadSonaxScript = useCallback(async () => {
    const currentPath = window.location.pathname;
    const isPageAllowed = currentPath !== '/auth' && currentPath !== '/';
    
    // Não carrega o script se não estiver em uma página permitida
    if (!isPageAllowed) {
      return;
    }

    // Não carrega o script se o usuário não estiver autenticado ou não tiver script configurado
    if (!user || userLoading || !user?.sonax_widget_script?.trim()) {
      return;
    }

    // Verifica limite de tentativas
    if (loadAttemptsRef.current >= maxLoadAttempts) {
      return;
    }

    // Verifica se já está carregando ou já foi carregado
    if (scriptLoadedRef.current || loadingInitiatedRef.current) {
      return;
    }

    // Verifica se o carregamento está demorando muito (timeout)
    const now = Date.now();
    if (loadingStartTimeRef.current > 0 && (now - loadingStartTimeRef.current) > maxLoadingTime) {
      scriptLoadedRef.current = false;
      loadingStartTimeRef.current = 0;
      loadingInitiatedRef.current = false;
      setIsLoading(false);
    }

    // Se ainda está carregando dentro do tempo limite, ignora
    if (loadingStartTimeRef.current > 0 && (now - loadingStartTimeRef.current) <= maxLoadingTime) {
      return;
    }
    loadAttemptsRef.current += 1;
    scriptLoadedRef.current = true; // Marca como carregando imediatamente
    loadingInitiatedRef.current = true; // Marca como iniciado
    loadingStartTimeRef.current = now; // Registra o momento do início
    setIsLoading(true);
    
    try {
      // Define variáveis globais necessárias para o script do Sonax
      // O script pagination.js do Sonax espera que domainurl esteja definido
      if (typeof (window as any).domainurl === 'undefined') {
        // Tenta extrair o domínio do Sonax da URL do script
        let sonaxDomain = null;
        
        // Procura por URLs do Sonax no script (webphone2.sonax.cloud ou similar)
        const sonaxUrlMatch = user.sonax_widget_script.match(/https?:\/\/([^\/"']+sonax[^\/"']*)/i);
        if (sonaxUrlMatch && sonaxUrlMatch[1]) {
          sonaxDomain = `https://${sonaxUrlMatch[1]}`;
        } else {
          // Tenta extrair de src="..." ou src='...'
          const scriptMatch = user.sonax_widget_script.match(/src=["']([^"']+)["']/);
          if (scriptMatch && scriptMatch[1]) {
            try {
              const scriptUrl = new URL(scriptMatch[1]);
              sonaxDomain = scriptUrl.origin;
            } catch {
              // Se não conseguir fazer parse, tenta extrair manualmente
              const urlMatch = scriptMatch[1].match(/https?:\/\/([^\/]+)/);
              if (urlMatch) {
                sonaxDomain = `https://${urlMatch[1]}`;
              }
            }
          }
        }
        
        // Define domainurl com o domínio do Sonax ou fallback para o domínio atual
        (window as any).domainurl = sonaxDomain || window.location.origin;
        console.log('🔧 [useSonaxWidget] domainurl definido como:', (window as any).domainurl);
      }

      // Remove script anterior se existir
      if (scriptElementRef.current) {
        try {
          // Verifica se o elemento ainda está conectado ao DOM
          if (scriptElementRef.current.isConnected) {
            scriptElementRef.current.remove();
          }
          scriptElementRef.current = null;
        } catch (error) {
          // Se já foi removido, apenas limpa a referência
          scriptElementRef.current = null;
        }
      }

      // Verifica se o script já foi carregado anteriormente
      const existingScript = document.querySelector('script[src*="webphone2.sonax.cloud"]');
      if (existingScript) {
        loadingStartTimeRef.current = 0; // Reset do timestamp
        loadingInitiatedRef.current = false; // Reset do flag de iniciado
        loadAttemptsRef.current = 0; // Reset do contador de tentativas
        setIsLoaded(true);
        setIsLoading(false);
        return;
      }

      // Verifica se já existe um script com ID específico do Sonax
      const existingSonaxScript = document.querySelector('script[id*="sonax"], script[id*="webphone"]');
      if (existingSonaxScript) {
        loadingStartTimeRef.current = 0; // Reset do timestamp
        loadingInitiatedRef.current = false; // Reset do flag de iniciado
        loadAttemptsRef.current = 0; // Reset do contador de tentativas
        setIsLoaded(true);
        setIsLoading(false);
        return;
      }

      // Extrai o conteúdo do script removendo as tags <script>
      let scriptContent = user.sonax_widget_script.trim();
      
      // Remove as tags <script> e </script> se existirem
      scriptContent = scriptContent.replace(/<\/?script[^>]*>/gi, '');
      
      // Tenta injetar o script original como HTML (abordagem principal)
      try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = user.sonax_widget_script;
        const originalScript = tempDiv.querySelector('script');
        
        if (originalScript) {
          const newScript = document.createElement('script');
          newScript.src = originalScript.src;
          newScript.id = originalScript.id || 'sonax-widget-script';
          newScript.async = true;

          // Marca como carregado SOMENTE quando o script externo terminar de executar
          newScript.onload = () => {
            loadingStartTimeRef.current = 0;
            loadingInitiatedRef.current = false;
            loadAttemptsRef.current = 0;
            setIsLoaded(true);
            setIsLoading(false);
            console.log('✅ Script Sonax carregado e executado com sucesso');
          };
          newScript.onerror = () => {
            loadingStartTimeRef.current = 0;
            loadingInitiatedRef.current = false;
            scriptLoadedRef.current = false;
            setIsLoading(false);
            console.error('❌ Erro ao carregar script externo do Sonax');
          };

          // Adiciona ao head
          document.head.appendChild(newScript);
          scriptElementRef.current = newScript;

          // Retorna aqui — onload/onerror cuidam do estado
          return;
        }
      } catch (htmlError) {
        // Fallback: tenta com eval
        try {
          eval(scriptContent);
        } catch (evalError) {
          // Ignora erro de eval
        }
      }
      // Fallback para quando nao encontrou tag <script> com src (ex: script inline via eval)
      loadingStartTimeRef.current = 0;
      loadingInitiatedRef.current = false;
      loadAttemptsRef.current = 0;
      setIsLoaded(true);
      setIsLoading(false);
      
    } catch (error) {
      console.error('❌ Erro ao carregar script do Sonax:', error);
      loadingStartTimeRef.current = 0; // Reset do timestamp
      loadingInitiatedRef.current = false; // Reset do flag de iniciado
      scriptLoadedRef.current = false; // Reset do estado de carregamento
      // Não reseta loadAttemptsRef aqui para permitir retry até o limite
      setIsLoading(false);
    }
  }, [user, userLoading, user?.sonax_widget_script]);

  // Função para limpar scripts do Sonax quando não estiver em página permitida
  const cleanupSonaxScripts = useCallback(() => {
    // Remove script injetado pelo hook
    if (scriptElementRef.current) {
      try {
        // Verifica se o elemento ainda está conectado ao DOM
        if (scriptElementRef.current.isConnected) {
          // Usa remove() que é mais seguro e moderno
          scriptElementRef.current.remove();
        }
        scriptElementRef.current = null;
      } catch (error) {
        // Se já foi removido, apenas limpa a referência
        scriptElementRef.current = null;
      }
    }

    // Remove scripts existentes do Sonax
    const existingScripts = document.querySelectorAll('script[src*="webphone2.sonax.cloud"], script[id*="sonax"], script[src*="sonax"]');
    existingScripts.forEach(script => {
      try {
        script.remove();
      } catch (error) {
        // Ignora erro se já foi removido
      }
    });

    // Remove SOMENTE o widget injetado (div.SonaxWidget e subárvore). NÃO usar [class*="sonax"]
    // genérico — isso casaria com componentes do CRM (ex: .sonax-header-btn) e os apagaria.
    const widgetRoots = document.querySelectorAll('div.SonaxWidget');
    widgetRoots.forEach(element => {
      try {
        element.remove();
      } catch (error) {
        // Ignora erro se já foi removido
      }
    });

    // Reset dos estados
    scriptLoadedRef.current = false;
    loadingStartTimeRef.current = 0; // Reset do timestamp
    loadingInitiatedRef.current = false; // Reset do flag de iniciado
    loadAttemptsRef.current = 0; // Reset do contador de tentativas
    setIsLoaded(false);
    setIsActive(false);
    setIsProcessing(false);
  }, []);

  // Monitora mudanças de rota para limpar scripts quando necessário
  useEffect(() => {
    const currentPath = window.location.pathname;
    
    // Só executa se a rota realmente mudou
    if (lastPathRef.current === currentPath) {
      return;
    }
    
    lastPathRef.current = currentPath;
    const isPageAllowed = currentPath !== '/auth' && currentPath !== '/';
    
    // Limpa scripts quando navega para páginas não permitidas
    if (!isPageAllowed) {
      cleanupSonaxScripts();
    }
  }, [cleanupSonaxScripts]);

  // Sistema de interceptação de logs do Sonax com handlers configuráveis.
  // Só intercepta o console quando ARMADO (operate / click-to-call) — fora disso nenhuma
  // ligação pode ocorrer, então não há o que detectar e o console fica limpo. Quando desarma,
  // o cleanup restaura o console original.
  useEffect(() => {
    if (!armed) return;
    try {
      // IMPORTANTE: Usa as funções originais salvas no HTML (configuração precoce)
      // Se não existirem, cria fallbacks seguros
      const safeOriginalLog = (window as any).__originalConsoleLog || (() => {
        try {
          const original = console.log.bind(console);
          // Salva para uso futuro
          (window as any).__originalConsoleLog = original;
          return original;
        } catch {
          return (...args: any[]) => { try { console.log(...args); } catch {} };
        }
      })();
      const safeOriginalWarn = (window as any).__originalConsoleWarn || (() => {
        try {
          return console.warn.bind(console);
        } catch {
          return (...args: any[]) => { try { console.warn(...args); } catch {} };
        }
      })();
      const safeOriginalError = (window as any).__originalConsoleError || (() => {
        try {
          return console.error.bind(console);
        } catch {
          return (...args: any[]) => { try { console.error(...args); } catch {} };
        }
      })();
      const safeOriginalInfo = (window as any).__originalConsoleInfo || (() => {
        try {
          return console.info?.bind(console) || safeOriginalLog;
        } catch {
          return safeOriginalLog;
        }
      })();
      const safeOriginalDebug = (window as any).__originalConsoleDebug || (() => {
        try {
          return console.debug?.bind(console) || safeOriginalLog;
        } catch {
          return safeOriginalLog;
        }
      })();
      const safeOriginalTrace = (window as any).__originalConsoleTrace || (() => {
        try {
          return console.trace?.bind(console) || safeOriginalLog;
        } catch {
          return safeOriginalLog;
        }
      })();
      
      // Salva globalmente ANTES de qualquer uso
      (window as any).__originalConsoleLog = safeOriginalLog;
      (window as any).__originalConsoleWarn = safeOriginalWarn;
      (window as any).__originalConsoleError = safeOriginalError;
      (window as any).__originalConsoleInfo = safeOriginalInfo;
      (window as any).__originalConsoleDebug = safeOriginalDebug;
      (window as any).__originalConsoleTrace = safeOriginalTrace;
      
      // Agora pode usar os originais com segurança
      // Kill switch de logs: janela ou localStorage (__DISABLE_SONAX_LOGS = "true")
      const disableLogs = Boolean(
        (window as any).__DISABLE_SONAX_LOGS ||
        (typeof localStorage !== 'undefined' && localStorage.getItem('__DISABLE_SONAX_LOGS') === 'true')
      );
      if (disableLogs) {
        safeOriginalLog('[useSonaxWidget] 🔇 Logs do Sonax desativados por __DISABLE_SONAX_LOGS');
        // Não intercepta nem substitui console.*
        return () => {};
      }

      safeOriginalLog('[useSonaxWidget] 🔧 Iniciando configuração de interceptação de logs do Sonax...');
      safeOriginalLog('[useSonaxWidget] 🌐 Navegador:', navigator.userAgent);
      safeOriginalLog('[useSonaxWidget] 🔍 Console original salvo com sucesso');
      
      // Se já foi interceptado, restaura primeiro
      if ((window as any).__sonaxLogIntercepted) {
        safeOriginalLog('[useSonaxWidget] ⚠️ Interceptação já existe, restaurando funções originais primeiro...');
        if ((window as any).__originalConsoleLog) {
          console.log = (window as any).__originalConsoleLog;
          console.warn = (window as any).__originalConsoleWarn;
          console.error = (window as any).__originalConsoleError;
          if ((window as any).__originalConsoleInfo) console.info = (window as any).__originalConsoleInfo;
          if ((window as any).__originalConsoleDebug) console.debug = (window as any).__originalConsoleDebug;
          if ((window as any).__originalConsoleTrace) console.trace = (window as any).__originalConsoleTrace;
        }
      }

      // Usa as funções originais salvas
      const originalLog = safeOriginalLog;
      const originalWarn = safeOriginalWarn;
      const originalError = safeOriginalError;
      const originalInfo = safeOriginalInfo;
      const originalDebug = safeOriginalDebug;
      const originalTrace = safeOriginalTrace;

      // Função auxiliar para extrair telefone do log SIP
      const extractPhoneFromSipLog = (message: string): string | null => {
      originalLog(`🔍 [SONAX] Tentando extrair telefone de: ${message.substring(0, 200)}...`);
      
      // Piso de 10 dígitos em TODOS os padrões: o destino do SIP costuma ser um
      // ramal/extensão curto (ex.: "sip:42@"), que antes vazava como telefone "lixo"
      // de 2-3 dígitos para a fila. Um telefone BR válido tem >= 10 dígitos.

      // Tenta primeiro extrair diretamente da linha SEND: ACK (mais comum)
      const ackMatch = message.match(/SEND:\s*ACK\s+sip:(\d{10,15})@/i);
      if (ackMatch && ackMatch[1]) {
        originalLog(`✅ [SONAX] Telefone extraído do ACK: ${ackMatch[1]}`);
        return ackMatch[1];
      }

      // Procura pelo campo "To:" no log (pode estar em múltiplas linhas)
      const toMatch = message.match(/To:\s*<sip:(\d{10,15})@/i);
      if (toMatch && toMatch[1]) {
        originalLog(`✅ [SONAX] Telefone extraído do To: ${toMatch[1]}`);
        return toMatch[1];
      }
      
      // Tenta padrão mais genérico: sip:numero@
      const genericMatch = message.match(/sip:(\d{10,15})@/i);
      if (genericMatch && genericMatch[1]) {
        originalLog(`✅ [SONAX] Telefone extraído (genérico): ${genericMatch[1]}`);
        return genericMatch[1];
      }
      
      originalLog(`⚠️ [SONAX] Não foi possível extrair telefone`);
      return null;
      };

      // Define handlers para logs específicos
      // Cada handler é um objeto com: pattern (regex ou string) e action (função a executar)
      const logHandlers: Array<{
      pattern: RegExp | string;
      action: (message: string, ...args: any[]) => void | Promise<void>;
      description?: string;
    }> = [
      {
        pattern: /coonected start startTimer/i,
        description: 'Detecta quando uma chamada é estabelecida (conexão iniciada e timer iniciado)',
        action: async (message: string, ...args: any[]) => {
          const now = new Date();
          const dataInicio = now.toISOString().split('T')[0];
          const horaInicio = now.toTimeString().split(' ')[0].substring(0, 5);

          originalLog('✅ [SONAX] 🟢 INÍCIO DA LIGAÇÃO DETECTADO');
          originalLog(`📞 [SONAX] "coonected start startTimer" capturado — guardando dados da ligação`);
          originalLog(`📅 [SONAX] Data início: ${dataInicio}, Hora início: ${horaInicio}`);

          const prev = callDataRef.current;
          callDataRef.current = {
            data_inicio: dataInicio,
            hora_inicio: horaInicio,
            telefone: prev?.telefone,
            deal_id: prev?.deal_id,
          };

          hangupLockRef.current = false;
          lastHangupExecutionRef.current = 0;

          if (processedCallsSet.size > 10) {
            const entries = Array.from(processedCallsSet);
            processedCallsSet.clear();
            entries.slice(-5).forEach((id) => processedCallsSet.add(id));
          }

          if (callDataRef.current && !callDataRef.current.telefone) {
            for (let i = recentLogs.length - 1; i >= 0 && i >= recentLogs.length - 10; i--) {
              const phone = extractPhoneFromSipLog(recentLogs[i]);
              if (phone) {
                originalLog(`📱 [SONAX] Telefone extraído dos logs recentes: ${phone}`);
                callDataRef.current.telefone = phone;
                break;
              }
            }
          }
        }
      },
      {
        pattern: /PeerConnection::stop\(\)/i,
        description: 'Detecta quando uma chamada é terminada (PeerConnection parado)',
        action: async (message: string, ...args: any[]) => {
          const now = new Date();
          const timestamp = now.getTime();

          // O registro da ligação no CRM não sai mais daqui: a Sonax chama a edge function
          // sonax-webhook no hangup, e a intenção de discagem (call_dial_intents, gravada por
          // useCallProvider) liga a ligação à atividade. Antes este handler fazia POST sem
          // autenticação para o n8n (crm-sonax-call-queue), que só enchia sonax_call_queue.
          // Aqui fica apenas a conclusão da atividade de origem, uma vez por ligação.

          // Cria um identificador único para esta ligação
          // Usa telefone + data/hora de início (se disponível) ou timestamp atual
          const callId = callDataRef.current?.telefone
            ? `${callDataRef.current.telefone}_${callDataRef.current.data_inicio}_${callDataRef.current.hora_inicio}`
            : `unknown_${timestamp}`;

          // PROTEÇÃO CONTRA EXECUÇÕES MÚLTIPLAS - o stop() aparece várias vezes por ligação:
          // 1. Verifica se esta ligação específica já foi processada (usando Set global)
          if (processedCallsSet.has(callId)) {
            originalLog(`🔒 [SONAX] Ligação ${callId} já foi processada, ignorando...`);
            return;
          }

          // 2. Verifica se já está sendo processado (lock ativo)
          if (hangupLockRef.current) {
            originalLog('🔒 [SONAX] Handler já está sendo executado, ignorando chamada duplicada...');
            return;
          }

          // 3. Verifica se foi executado muito recentemente (dentro de 5 segundos)
          if (timestamp - lastHangupExecutionRef.current < 5000) {
            originalLog('🔒 [SONAX] Handler executado muito recentemente, ignorando chamada duplicada...');
            return;
          }

          processedCallsSet.add(callId);
          hangupLockRef.current = true;
          lastHangupExecutionRef.current = timestamp;

          try {
            originalLog('✅ [SONAX] 🔴 FIM DA LIGAÇÃO DETECTADO');
            originalLog('📞 [SONAX] "PeerConnection::stop()" capturado - Ligação encerrada');
            originalLog(`📋 [SONAX] Mensagem: ${message.substring(0, 200)}`);

            callDataRef.current = null;

            const aid = pendingCrmActivityIdRef.current;
            if (aid) {
              pendingCrmActivityIdRef.current = null;
              window.dispatchEvent(
                new CustomEvent('mengage:complete-activity-from-call', { detail: { activityId: aid } })
              );
            }
          } catch (error) {
            originalError('❌ [SONAX] Erro no handler de fim de ligação:', error);
            processedCallsSet.delete(callId);
          } finally {
            // SEMPRE libera o lock, mas mantém o timestamp para prevenir execuções muito próximas
            hangupLockRef.current = false;
          }
        }
      }
      ];

      // Armazena logs recentes para capturar logs multi-linha
      const recentLogs: string[] = [];
      const MAX_RECENT_LOGS = 10;

      // Função para processar logs do Sonax
      const processSonaxLog = (level: string, ...args: any[]) => {
      // Converte todos os argumentos em uma string única
      const message = args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' ');

      // VERIFICAÇÃO ESPECIAL PARA PeerConnection - captura ANTES de qualquer filtro
      // Isso garante que vamos capturar mesmo que não seja identificado como log do Sonax
      const hasPeerConnection = message.includes('PeerConnection') || args.some(a => String(a).includes('PeerConnection'));
      
      if (hasPeerConnection) {
        if (SONAX_DEBUG) {
          originalLog(`🔍 [SONAX DEBUG] ⚠️⚠️⚠️ PeerConnection ENCONTRADO ANTES DO FILTRO! ⚠️⚠️⚠️`);
          originalLog(`🔍 [SONAX DEBUG] Level: ${level}`);
          originalLog(`🔍 [SONAX DEBUG] Mensagem completa: "${message}"`);
          originalLog(`🔍 [SONAX DEBUG] Args originais (${args.length}):`, args);
          originalLog(`🔍 [SONAX DEBUG] Args como string: ${args.map(a => String(a).substring(0, 200)).join(' | ')}`);
        }

        // Se contém "PeerConnection::stop()", força o processamento mesmo que não seja identificado como log do Sonax
        if (message.includes('PeerConnection::stop()') || args.some(a => String(a).includes('PeerConnection::stop()'))) {
          if (SONAX_DEBUG) originalLog(`🔍 [SONAX DEBUG] 🎯 FORÇANDO PROCESSAMENTO DE PeerConnection::stop()!`);
          // Processa diretamente sem verificar isSonaxLog
          for (const handler of logHandlers) {
            const pattern = typeof handler.pattern === 'string' 
              ? new RegExp(handler.pattern, 'i')
              : handler.pattern;
            
            if (pattern.test(message) || args.some(a => pattern.test(String(a)))) {
              originalLog(`✅ [SONAX] Handler FORÇADO acionado! Padrão: ${handler.description || handler.pattern}`);
              try {
                const result = handler.action(message, ...args);
                if (result instanceof Promise) {
                  result.catch((error) => {
                    originalError('[SONAX] Erro ao executar handler de log:', error);
                  });
                }
              } catch (error) {
                originalError('[SONAX] Erro ao executar handler de log:', error);
              }
              break; // Para após encontrar o primeiro handler correspondente
            }
          }
        }
      }

      // IGNORA logs de debug do próprio sistema (evita loop infinito)
      // MAS NÃO ignora logs importantes do Sonax mesmo que venham através do nosso sistema
      if ((message.includes('[SONAX DEBUG]') || 
          message.includes('[SONAX] Handler acionado') ||
          message.includes('[SONAX] Telefone extraído') ||
          message.includes('[SONAX] Chamada estabelecida') ||
          message.includes('useSonaxWidget') ||
          message.includes('Interceptação')) &&
          !message.includes('PeerConnection::stop()') &&
          !message.includes('coonected start startTimer')) {
        return; // Não processa logs de debug, exceto os logs importantes do Sonax
      }

      // IGNORA logs de teste
      if (message.includes('@test.com') || message.includes('1234567890')) {
        return; // Não processa logs de teste
      }

      // Adiciona à lista de logs recentes
      recentLogs.push(message);
      if (recentLogs.length > MAX_RECENT_LOGS) {
        recentLogs.shift();
      }

      // Cria uma mensagem combinada dos últimos logs (para capturar logs multi-linha)
      const combinedMessage = recentLogs.join('\n');

      // Verifica se é log REAL do Sonax (não logs de debug)
      // Os logs do Sonax podem vir de várias formas:
      // 1. "SIPml-api.js:1 SEND: ACK..." (com prefixo do navegador)
      // 2. "SEND: ACK..." (sem prefixo, quando capturado diretamente)
      // 3. "recv=BYE..." (respostas recebidas)
      // 4. "coonected start startTimer" (início da ligação)
      // 5. "PeerConnection::stop()" (fim da ligação)
      
      // Primeiro verifica se contém padrões específicos importantes (não filtra por [SONAX)
      const hasImportantPattern = 
        message.includes('coonected start startTimer') ||
        message.includes('PeerConnection::stop()') ||
        message.includes('PeerConnection::');
      
      // Verifica também nos argumentos originais (antes de concatenar)
      const hasImportantPatternInArgs = args.some(arg => {
        const argStr = String(arg);
        return argStr.includes('coonected start startTimer') ||
               argStr.includes('PeerConnection::stop()') ||
               argStr.includes('PeerConnection::');
      });
      
      const isSonaxLog = 
        !message.includes('[SONAX') &&
        !message.includes('useSonaxWidget') &&
        !message.includes('Interceptação') &&
        (
          hasImportantPattern || // Permite logs importantes mesmo que venham de outros lugares
          hasImportantPatternInArgs || // Verifica também nos args originais
          // Logs que começam com indicadores SIP
          message.startsWith('SEND:') ||
          message.startsWith('recv=') ||
          message.includes('SIPml-api.js') ||
          // Ou contém padrões específicos do SIP
          (message.includes('INVITE') && message.includes('sip:')) ||
          (message.includes('ACK') && message.includes('sip:')) ||
          (message.includes('BYE') && message.includes('sip:')) ||
          message.includes('=== INVITE Dialog terminated ===') ||
          message.includes('State machine:') ||
          message.includes('__tsip_transport') ||
          // Verifica nos argumentos originais também
          args.some(arg => {
            const argStr = String(arg);
            return (argStr.includes('SIPml-api.js') || 
                   argStr.startsWith('SEND:') || 
                   argStr.startsWith('recv=') ||
                   argStr.includes('coonected start startTimer') ||
                   argStr.includes('PeerConnection::stop()') ||
                   argStr.includes('PeerConnection::') ||
                   (argStr.includes('sip:') && (argStr.includes('INVITE') || argStr.includes('ACK') || argStr.includes('BYE')))) &&
                   !argStr.includes('[SONAX');
          })
        );

      // Log de debug para PeerConnection (controlado por flag)
      if (SONAX_DEBUG && (message.includes('PeerConnection') || args.some(a => String(a).includes('PeerConnection')))) {
        originalLog(`🔍 [SONAX DEBUG] ⚠️ PeerConnection encontrado!`);
        originalLog(`🔍 [SONAX DEBUG] Mensagem processada: "${message}"`);
        originalLog(`🔍 [SONAX DEBUG] isSonaxLog: ${isSonaxLog}`);
        originalLog(`🔍 [SONAX DEBUG] Args originais: ${args.map(a => String(a).substring(0, 150)).join(' | ')}`);
        originalLog(`🔍 [SONAX DEBUG] Vai processar? ${isSonaxLog ? 'SIM' : 'NÃO'}`);
      }

      if (isSonaxLog) {
        // Verifica cada handler para ver se o padrão corresponde
        // Testa tanto na mensagem atual quanto na mensagem combinada (para logs multi-linha)
        for (const handler of logHandlers) {
          const pattern = typeof handler.pattern === 'string' 
            ? new RegExp(handler.pattern, 'i')
            : handler.pattern;
          
          // Testa na mensagem atual e na mensagem combinada
          const matchesCurrent = pattern.test(message);
          const matchesCombined = pattern.test(combinedMessage);
          
          // Log de debug para PeerConnection::stop() (controlado por flag)
          if (SONAX_DEBUG && handler.pattern.toString().includes('PeerConnection')) {
            originalLog(`🔍 [SONAX DEBUG] Testando padrão PeerConnection::stop()`);
            originalLog(`🔍 [SONAX DEBUG] matchesCurrent: ${matchesCurrent}, matchesCombined: ${matchesCombined}`);
            originalLog(`🔍 [SONAX DEBUG] message: "${message}"`);
            originalLog(`🔍 [SONAX DEBUG] combinedMessage (últimos 3): "${recentLogs.slice(-3).join(' | ')}"`);
          }
          
          if (matchesCurrent || matchesCombined) {
            originalLog(`✅ [SONAX] Handler acionado! Padrão: ${handler.description || handler.pattern}`);
            originalLog(`✅ [SONAX] Mensagem: ${message.substring(0, 200)}`);
            
            // Usa a mensagem combinada se o padrão corresponder a ela
            const messageToProcess = matchesCombined ? combinedMessage : message;
            
            // Executa a ação do handler (suporta funções assíncronas)
            try {
              const result = handler.action(messageToProcess, ...args);
              // Se retornar uma Promise, trata o erro assincronamente
              if (result instanceof Promise) {
                result.catch((error) => {
                  originalError('[SONAX] Erro ao executar handler de log:', error);
                });
              }
            } catch (error) {
              originalError('[SONAX] Erro ao executar handler de log:', error);
            }
          }
        }
      }
      };

      // Função auxiliar para interceptar qualquer método do console
      const interceptConsoleMethod = (methodName: string, originalMethod: (...args: any[]) => void) => {
      return function(...args: any[]) {
        // Log de debug para verificar se PeerConnection está sendo interceptado
        if (SONAX_DEBUG && args.some(a => String(a).includes('PeerConnection'))) {
          originalLog(`🔍 [SONAX DEBUG] 🎯 console.${methodName} interceptado com PeerConnection!`, args);
        }
        processSonaxLog(methodName, ...args);
        originalMethod.apply(console, args);
      };
      };

      // Substitui console.log
      console.log = interceptConsoleMethod('log', originalLog);

      // Substitui console.warn
      console.warn = interceptConsoleMethod('warn', originalWarn);

      // Substitui console.error
      console.error = interceptConsoleMethod('error', originalError);

      // Substitui console.info (se existir)
      if (console.info) {
        console.info = interceptConsoleMethod('info', originalInfo);
      }

      // Substitui console.debug (se existir)
      if (console.debug) {
        console.debug = interceptConsoleMethod('debug', originalDebug);
      }

      // Substitui console.trace (se existir)
      if (console.trace) {
        console.trace = interceptConsoleMethod('trace', originalTrace);
      }

      // Marca como interceptado
      (window as any).__sonaxLogIntercepted = true;
      (window as any).__sonaxLogForceReconfigure = false;
      
      originalLog('[useSonaxWidget] ✅ Interceptação de logs do Sonax configurada com sucesso!');
      originalLog('[useSonaxWidget] ✅ Console.log interceptado:', typeof console.log);
      originalLog('[useSonaxWidget] ✅ Console.warn interceptado:', typeof console.warn);
      originalLog('[useSonaxWidget] ✅ Console.error interceptado:', typeof console.error);
      
      // Verifica se a interceptação está funcionando
      setTimeout(() => {
        const testMessage = '__SONAX_TEST_INTERCEPTION__';
        let intercepted = false;
        const testOriginalLog = console.log;
        const testWrapper = function(...args: any[]) {
          if (args.some(a => String(a).includes(testMessage))) {
            intercepted = true;
          }
          testOriginalLog.apply(console, args);
        };
        console.log = testWrapper as any;
        console.log(testMessage);
        console.log = testOriginalLog;
        
        if (intercepted) {
          originalLog('[useSonaxWidget] ✅ Verificação: Interceptação está funcionando corretamente');
        } else {
          originalError('[useSonaxWidget] ⚠️ Verificação: Interceptação pode não estar funcionando corretamente');
        }
      }, 100);
      
    } catch (error) {
      // Em caso de erro, tenta logar usando o console original se disponível
      try {
        const fallbackLog = (window as any).__originalConsoleLog || console.log.bind(console);
        fallbackLog('[useSonaxWidget] ❌ ERRO ao configurar interceptação:', error);
        fallbackLog('[useSonaxWidget] ❌ Erro completo:', error instanceof Error ? error.stack : String(error));
      } catch {
        // Se até o fallback falhar, não há muito o que fazer
      }
    }

    // Cleanup: restaura funções originais quando componente desmonta
    return () => {
      try {
        if ((window as any).__originalConsoleLog) {
          console.log = (window as any).__originalConsoleLog;
          console.warn = (window as any).__originalConsoleWarn;
          console.error = (window as any).__originalConsoleError;
          if ((window as any).__originalConsoleInfo) console.info = (window as any).__originalConsoleInfo;
          if ((window as any).__originalConsoleDebug) console.debug = (window as any).__originalConsoleDebug;
          if ((window as any).__originalConsoleTrace) console.trace = (window as any).__originalConsoleTrace;
          (window as any).__sonaxLogIntercepted = false;
        }
      } catch (error) {
        // Ignora erros no cleanup
      }
    };
  }, [armed]); // (Re)configura/desfaz a interceptação conforme o gate "armado"

  // Carrega o script automaticamente quando usuário está logado (exceto na tela de auth)
  // Efeito para definir domainurl globalmente antes de qualquer script do Sonax ser executado
  useEffect(() => {
    if (user?.sonax_widget_script) {
      // Define domainurl globalmente antes de qualquer script do Sonax ser executado
      if (typeof (window as any).domainurl === 'undefined') {
        // Tenta extrair o domínio do Sonax da URL do script
        let sonaxDomain = null;
        
        // Procura por URLs do Sonax no script (webphone2.sonax.cloud ou similar)
        const sonaxUrlMatch = user.sonax_widget_script.match(/https?:\/\/([^\/"']+sonax[^\/"']*)/i);
        if (sonaxUrlMatch && sonaxUrlMatch[1]) {
          sonaxDomain = `https://${sonaxUrlMatch[1]}`;
        } else {
          // Tenta extrair de src="..." ou src='...'
          const scriptMatch = user.sonax_widget_script.match(/src=["']([^"']+)["']/);
          if (scriptMatch && scriptMatch[1]) {
            try {
              const scriptUrl = new URL(scriptMatch[1]);
              sonaxDomain = scriptUrl.origin;
            } catch {
              // Se não conseguir fazer parse, tenta extrair manualmente
              const urlMatch = scriptMatch[1].match(/https?:\/\/([^\/]+)/);
              if (urlMatch) {
                sonaxDomain = `https://${urlMatch[1]}`;
              }
            }
          }
        }
        
        // Define domainurl com o domínio do Sonax ou fallback para o domínio atual
        (window as any).domainurl = sonaxDomain || window.location.origin;
        console.log('🔧 [useSonaxWidget] domainurl definido globalmente como:', (window as any).domainurl);
      }
    }
  }, [user?.sonax_widget_script]);

  // O script carrega APENAS quando ARMADO (operate / click-to-call / softphone do header).
  // Desarmado (ou fora de página permitida) → descarrega (ramal de-registra no SIP).
  useEffect(() => {
    // Limpa timeout anterior se existir
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
    }

    const currentPath = window.location.pathname;
    const isPageAllowed = currentPath !== '/auth' && currentPath !== '/';

    // Fora de página permitida (auth/root) → limpa de fato (saindo do app).
    if (!isPageAllowed) {
      cleanupSonaxScripts();
      return;
    }
    // Desarmado → NÃO carrega, mas também NÃO remove. O widget Sonax mantém um setInterval
    // próprio que re-registra o SIP sozinho (e que não temos como limpar): remover o
    // <script>/DOM não desregistra o ramal e ainda quebra o re-load (o interval acessa
    // #callBtn nulo + estado duplicado). Uma vez carregado (após armar), o widget persiste
    // até o refresh da página. O gate controla só o PRIMEIRO carregamento (boot/páginas comuns
    // não registram o ramal); sair do operate/fila usa logout_agent pra não receber do dialer.
    if (!armed) {
      return;
    }

    // Verifica se o script já está carregado no DOM
    const existingScript = document.querySelector('script[src*="webphone2.sonax.cloud"]');
    const existingSonaxScript = document.querySelector('script[id*="sonax"], script[id*="webphone"]');

    // Se o script já existe no DOM, marca como carregado e não tenta carregar novamente
    if (existingScript || existingSonaxScript) {
      // Usa ref para evitar re-renders desnecessários
      if (!scriptLoadedRef.current) {
        scriptLoadedRef.current = true;
        loadingInitiatedRef.current = false;
        loadingStartTimeRef.current = 0;
        setIsLoaded(true);
      }
      return;
    }

    // Só carrega quando armado + página permitida + usuário autenticado + script configurado.
    if (isPageAllowed && armed && user && !userLoading && hasScript) {
      // Verifica se não há carregamento em andamento
      const now = Date.now();
      if (loadingStartTimeRef.current > 0 && (now - loadingStartTimeRef.current) <= maxLoadingTime) {
        return;
      }

      // Se já está carregado ou carregando, não tenta novamente
      if (scriptLoadedRef.current || loadingInitiatedRef.current) {
        return;
      }

      // Carrega imediatamente sem debounce para garantir que carregue o mais rápido possível
      loadSonaxScript();
    }

    // Cleanup do timeout
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, [user, userLoading, hasScript, armed, loadSonaxScript, cleanupSonaxScripts]);

  // Detecta navegação via React Router e recarrega o script se necessário
  // Não usa useLocation (hook renderiza fora do Router), usa polling leve
  useEffect(() => {
    let lastPath = window.location.pathname;
    const interval = setInterval(() => {
      const currentPath = window.location.pathname;
      if (currentPath !== lastPath) {
        lastPath = currentPath;
        const isAllowed = currentPath !== '/auth' && currentPath !== '/';
        // Só recarrega ao navegar se estiver ARMADO (não re-injeta o script em página comum).
        if (isAllowed && isSonaxArmed() && user && !userLoading && hasScript && !scriptLoadedRef.current && !loadingInitiatedRef.current) {
          loadSonaxScript();
        }
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [user, userLoading, hasScript, loadSonaxScript]);

  // Monitora timeout do carregamento
  useEffect(() => {
    if (!isLoading || loadingStartTimeRef.current === 0) {
      return;
    }

    const timeoutId = setTimeout(() => {
      const now = Date.now();
      if (loadingStartTimeRef.current > 0 && (now - loadingStartTimeRef.current) > maxLoadingTime) {
        scriptLoadedRef.current = false;
        loadingStartTimeRef.current = 0;
        setIsLoading(false);
      }
    }, maxLoadingTime);

    return () => clearTimeout(timeoutId);
  }, [isLoading, maxLoadingTime]);

  // Monitor z-index DESABILITADO — controle centralizado no SonaxHeaderButton.
  // Apenas esconde o widget na montagem inicial (o header button controla depois).
  useEffect(() => {
    if (!isLoaded) return;
    // Esconde o widget por padrão — o SonaxHeaderButton mostra quando necessário.
    // PORÉM: respeita o flag global __sonaxWidgetOpen. Se algum call site
    // (ex. focus execution mode) abriu o widget via openWidgetOnly antes
    // deste timeout disparar, não fechar o que acabou de ser aberto.
    const timeout = setTimeout(() => {
      if ((window as unknown as { __sonaxWidgetOpen?: boolean }).__sonaxWidgetOpen === true) return;
      const containers = document.querySelectorAll('div.SonaxWidget');
      containers.forEach((el) => {
        const h = el as HTMLElement;
        if (h.parentElement?.tagName === 'BODY') {
          h.style.setProperty('display', 'none', 'important');
        }
      });
    }, 1500);
    return () => clearTimeout(timeout);
  }, [isLoaded]);


  // Função para fazer chamada
  const makeCall = useCallback(async (phoneNumber: string): Promise<void> => {
    if (!isLoaded || !hasScript) {
      console.warn('Widget Sonax não está carregado ou configurado');
      return;
    }

    try {
      const windowObj = window as any;
      
      // Aguarda um pouco para garantir que os objetos estejam disponíveis
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Tenta diferentes métodos para fazer chamada
      if (windowObj.Sonax) {
        if (typeof windowObj.Sonax.call === 'function') {
          windowObj.Sonax.call(phoneNumber);
        } else if (typeof windowObj.Sonax.makeCall === 'function') {
          windowObj.Sonax.makeCall(phoneNumber);
        } else if (typeof windowObj.Sonax.dial === 'function') {
          windowObj.Sonax.dial(phoneNumber);
        }
      } else if (windowObj.sonaxWidget) {
        if (typeof windowObj.sonaxWidget.dial === 'function') {
          windowObj.sonaxWidget.dial(phoneNumber);
        } else if (typeof windowObj.sonaxWidget.call === 'function') {
          windowObj.sonaxWidget.call(phoneNumber);
        }
      } else if (windowObj.webphone) {
        if (typeof windowObj.webphone.call === 'function') {
          windowObj.webphone.call(phoneNumber);
        } else if (typeof windowObj.webphone.makeCall === 'function') {
          windowObj.webphone.makeCall(phoneNumber);
        }
      } else if (windowObj.loadScriptSonaxSip) {
        // Tenta usar a função loadScriptSonaxSip para fazer chamada
        try {
          if (typeof windowObj.loadScriptSonaxSip === 'function') {
            // Pode ser que a função aceite parâmetros
            windowObj.loadScriptSonaxSip(phoneNumber);
          }
        } catch (error) {
          console.error('Erro ao usar loadScriptSonaxSip para chamada:', error);
        }
      } else {
        console.warn('Objetos do Sonax não encontrados para fazer chamada');
        
        // Tenta procurar por elementos do widget no DOM
        const widgetElements = document.querySelectorAll('[id*="sonax"], [class*="sonax"], [id*="webphone"], [class*="webphone"]');
        widgetElements.forEach((element) => {
          try {
            (element as HTMLElement).click();
          } catch (error) {
            // Ignora erro
          }
        });
      }
    } catch (error) {
      console.error('Erro ao fazer chamada Sonax:', error);
    }
  }, [isAllowedPage, user, userLoading, isLoaded, hasScript]);

  // Função para tentar popular o campo do discador
  const populateDialerField = useCallback(async (phoneNumber: string): Promise<void> => {
    
    // Aguarda um pouco para o widget carregar completamente
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Procura especificamente pelo campo do discador do Sonax
    const phoneInput = document.querySelector('#phoneNumber') as HTMLInputElement;
    if (phoneInput) {
      // Limpa o campo primeiro
      phoneInput.value = '';
      phoneInput.focus();
      
      // Simula digitação caractere por caractere
      for (let i = 0; i < phoneNumber.length; i++) {
        const char = phoneNumber[i];
        phoneInput.value += char;
        phoneInput.dispatchEvent(new KeyboardEvent('keydown', { key: char, bubbles: true }));
        phoneInput.dispatchEvent(new KeyboardEvent('keypress', { key: char, bubbles: true }));
        phoneInput.dispatchEvent(new KeyboardEvent('keyup', { key: char, bubbles: true }));
        phoneInput.dispatchEvent(new Event('input', { bubbles: true }));
        
        // Pequena pausa entre caracteres para simular digitação real
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      phoneInput.dispatchEvent(new Event('change', { bubbles: true }));
      phoneInput.dispatchEvent(new Event('blur', { bubbles: true }));
      return;
    }
    
    // Fallback: procura por campos de input no widget
    const inputFields = document.querySelectorAll('input[type="text"], input[placeholder*="número"], input[placeholder*="Insira"], input[placeholder*="phone"], input[placeholder*="telefone"]');
    
    inputFields.forEach((input) => {
      const inputElement = input as HTMLInputElement;
      
      // Tenta popular o campo
      try {
        inputElement.value = phoneNumber;
        inputElement.dispatchEvent(new Event('input', { bubbles: true }));
        inputElement.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (error) {
        // Ignora erro
      }
    });
    
    // Procura por elementos com classes específicas do Sonax
    const sonaxInputs = document.querySelectorAll('[class*="dialer"], [class*="phone"], [class*="number"], [id*="dialer"], [id*="phone"], [id*="number"]');
    
    sonaxInputs.forEach((element) => {
      if (element.tagName === 'INPUT') {
        const inputElement = element as HTMLInputElement;
        
        try {
          inputElement.value = phoneNumber;
          inputElement.dispatchEvent(new Event('input', { bubbles: true }));
          inputElement.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (error) {
          // Ignora erro
        }
      }
    });
    
    // Procura por elementos dentro de iframes
    const iframes = document.querySelectorAll('iframe');
    
    iframes.forEach((iframe) => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          const iframeInputs = iframeDoc.querySelectorAll('input[type="text"], input[placeholder*="número"], input[placeholder*="Insira"]');
          
          iframeInputs.forEach((input) => {
            const inputElement = input as HTMLInputElement;
            
            try {
              inputElement.value = phoneNumber;
              inputElement.dispatchEvent(new Event('input', { bubbles: true }));
              inputElement.dispatchEvent(new Event('change', { bubbles: true }));
            } catch (error) {
              // Ignora erro
            }
          });
        }
      } catch (error) {
        // Ignora erro
      }
    });
    
    // Tenta uma abordagem mais agressiva: simula digitação
    // Procura por todos os inputs visíveis
    const allInputs = document.querySelectorAll('input');
    
    allInputs.forEach((input) => {
      const inputElement = input as HTMLInputElement;
      const rect = inputElement.getBoundingClientRect();
      const isVisible = rect.width > 0 && rect.height > 0;
      
      if (isVisible) {
        try {
          // Foca no input
          inputElement.focus();
          
          // Limpa o campo
          inputElement.value = '';
          
          // Simula digitação
          for (let i = 0; i < phoneNumber.length; i++) {
            const char = phoneNumber[i];
            inputElement.value += char;
            inputElement.dispatchEvent(new Event('input', { bubbles: true }));
            inputElement.dispatchEvent(new Event('keydown', { bubbles: true }));
            inputElement.dispatchEvent(new Event('keyup', { bubbles: true }));
          }
          
          // Dispara eventos finais
          inputElement.dispatchEvent(new Event('change', { bubbles: true }));
          inputElement.dispatchEvent(new Event('blur', { bubbles: true }));
        } catch (error) {
          // Ignora erro
        }
      }
    });
  }, []);


  // Função simplificada para configurar áudio do Sonax
  const configureSonaxAudio = useCallback(async (): Promise<void> => {
    try {
      
      // Aguarda o Sonax carregar completamente
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Tenta configurar o áudio via API do Sonax
      const windowObj = window as any;
      
      if (windowObj.Sonax && typeof windowObj.Sonax.setAudioDevice === 'function') {
        windowObj.Sonax.setAudioDevice('default');
      }
      
      if (windowObj.sonaxWidget && typeof windowObj.sonaxWidget.setAudioDevice === 'function') {
        windowObj.sonaxWidget.setAudioDevice('default');
      }
      
      // Tenta configurar via DOM se necessário
      const audioSelect = document.querySelector('select[id*="audio"], select[class*="audio"]') as HTMLSelectElement;
      if (audioSelect) {
        audioSelect.value = 'default';
        audioSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
    } catch (error) {
    }
  }, []);

  // Função para tentar clicar no botão "Ligar" do widget
  const clickCallButton = useCallback(async (): Promise<void> => {
    
    // Aguarda um pouco para o widget estar pronto
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Procura pelo botão de ligar
    const callButton = document.querySelector('#callBtn') as HTMLButtonElement;
    if (callButton) {
      try {
        console.log('🔍 [clickCallButton] Clicando no botão de ligar:', callButton.textContent);
        callButton.click();
        return;
      } catch (error) {
        // Ignora erro
      }
    }
    
    // Fallback: procura por botões que possam ser o botão de ligar
    const possibleCallButtons = document.querySelectorAll('button[class*="call"], button[class*="ligar"], button[onclick*="startCall"], button[onclick*="call"]');
    
    possibleCallButtons.forEach((button) => {
      const buttonElement = button as HTMLButtonElement;
      
      try {
        buttonElement.click();
      } catch (error) {
        // Ignora erro
      }
    });
  }, []);

  // Função para tentar abrir o widget via DOM
  const openWidgetViaDOM = useCallback(async (phoneNumber: string): Promise<void> => {
    console.log('📞 [openWidgetViaDOM] Expandindo widget Sonax e preenchendo número...');
    const win = window as any;

    // O widget Sonax tem estrutura: div.SonaxWidget > div#widget > div#content > div#keyboard
    // O #keyboard contém o discador com input#phoneNumber e button#callBtn (onclick="startCall()")
    // Quando minimizado, o #content ou #keyboard fica com display:none
    // Funções globais do widget: startCall(), closeKeyboard(), pressKeyboard(), finishCall()

    // expandWidget() já foi chamado antes — ele posiciona e abre o widget corretamente.
    // Aqui só garantimos que #content esteja visível (container principal).
    // NÃO forçamos #keyboard — os painéis internos de #principal-content são mutuamente
    // exclusivos e gerenciados pelo Sonax. Forçar #keyboard durante chamada causa duplicação.
    const inner = getInnerContainer();

    if (inner) {
      const content = inner.querySelector('#content') as HTMLElement;
      const widgetEl = inner.querySelector('#widget') as HTMLElement;

      if (content) {
        content.style.setProperty('display', 'flex', 'important');
        content.style.setProperty('visibility', 'visible', 'important');
        content.style.setProperty('opacity', '1', 'important');
        console.log('✅ [openWidgetViaDOM] #content forçado visível');
      }
      if (widgetEl) {
        widgetEl.style.setProperty('display', 'block', 'important');
        widgetEl.style.setProperty('visibility', 'visible', 'important');
        console.log('✅ [openWidgetViaDOM] #widget forçado visível');
      }
    }

    // NÃO clica no #header — é um toggle e fecharia o widget que expandWidget() já abriu

    // Aguarda expansão
    await new Promise(resolve => setTimeout(resolve, 500));

    // Preenche o input#phoneNumber e inicia a chamada (scoped ao container Sonax)
    const phoneInput = (inner || document).querySelector('#phoneNumber') as HTMLInputElement;
    if (phoneInput) {
      phoneInput.focus();
      phoneInput.value = phoneNumber;
      phoneInput.dispatchEvent(new Event('input', { bubbles: true }));
      phoneInput.dispatchEvent(new Event('change', { bubbles: true }));
      console.log('✅ [openWidgetViaDOM] #phoneNumber preenchido com:', phoneNumber);
    } else {
      console.warn('⚠️ [openWidgetViaDOM] #phoneNumber não encontrado, tentando populateDialerField...');
      await populateDialerField(phoneNumber);
    }

    // Aguarda o número ser processado pelo widget e inicia a chamada
    await new Promise(resolve => setTimeout(resolve, 300));

    // Chama startCall() — função global do widget Sonax (onclick do #callBtn)
    if (typeof win.startCall === 'function') {
      console.log('📞 [openWidgetViaDOM] Chamando startCall()...');
      try { win.startCall(); } catch(e) { console.error('Erro ao chamar startCall:', e); }
    } else {
      // Fallback: clica no botão #callBtn (scoped)
      const callBtn = (inner || document).querySelector('#callBtn') as HTMLButtonElement;
      if (callBtn) {
        console.log('📞 [openWidgetViaDOM] Clicando em #callBtn...');
        callBtn.click();
      }
    }
  }, [populateDialerField]);

  // Funções para controlar visibilidade do widget
  const showWidget = useCallback(() => {
    // Se o header button está controlando, não interfere
    const headerControlled = (window as any).__sonaxWidgetOpen;
    if (headerControlled === true || headerControlled === false) {
      console.log('🔍 [showWidget] Header button no controle, pulando...');
      return;
    }
    console.log('🔍 [showWidget] Tentando mostrar widget Sonax...');
    const sonaxElements = document.querySelectorAll('[id*="sonax" i], [class*="sonax" i], [id*="webphone" i], [class*="webphone" i]');
    console.log('🔍 [showWidget] Elementos encontrados:', sonaxElements.length);
    
    if (sonaxElements.length === 0) {
      console.warn('⚠️ [showWidget] Nenhum elemento Sonax encontrado no DOM');
      return;
    }
    
    sonaxElements.forEach((element, index) => {
      const htmlElement = element as HTMLElement;
      if (htmlElement) {
        // Ignora o campo do formulário de configurações
        if (htmlElement.id === 'sonax_widget_script' || htmlElement.id?.includes('sonax_widget_script')) {
          return;
        }
        
        console.log(`✅ [showWidget] Mostrando elemento ${index + 1}:`, htmlElement.id || htmlElement.className);
        
        // Remove display: none e outros estilos que escondem o widget
        htmlElement.style.removeProperty('display');
        htmlElement.style.display = '';
        htmlElement.style.visibility = '';
        htmlElement.style.opacity = '';
        htmlElement.style.setProperty('pointer-events', 'auto', 'important');
        htmlElement.setAttribute('data-sonax-visible', 'true');
        
        // Garante z-index alto
        htmlElement.style.setProperty('z-index', '99999', 'important');
        htmlElement.style.setProperty('position', 'fixed', 'important');
        
        // Posiciona no canto inferior direito
        htmlElement.style.setProperty('right', '16px', 'important');
        htmlElement.style.setProperty('bottom', '16px', 'important');
        htmlElement.style.setProperty('top', 'auto', 'important');
        htmlElement.style.setProperty('left', 'auto', 'important');
        htmlElement.style.setProperty('margin', '0', 'important');
        htmlElement.style.setProperty('transform', 'none', 'important');

        // Esconde o botao nativo de fechar do Sonax
        const closeBtn = htmlElement.querySelector('#close-widget') as HTMLElement;
        if (closeBtn) closeBtn.style.setProperty('display', 'none', 'important');

        // Forca #content aberto (container principal)
        // NÃO forçamos #keyboard — painéis internos são mutuamente exclusivos
        const contentEl = htmlElement.querySelector('#content') as HTMLElement;
        if (contentEl) contentEl.style.setProperty('display', 'flex', 'important');
        
        // Força cor preta nos inputs do telefone dentro do widget
        const phoneInputs = htmlElement.querySelectorAll('input[type="text"], input[type="tel"], input[id*="phone"], input[id*="Phone"], #phoneNumber');
        phoneInputs.forEach((input) => {
          const inputElement = input as HTMLInputElement;
          if (inputElement) {
            inputElement.style.setProperty('color', '#000000', 'important');
            inputElement.style.setProperty('-webkit-text-fill-color', '#000000', 'important');
          }
        });
        
        // Aplica também a todos os descendentes
        htmlElement.querySelectorAll('*').forEach(child => {
          const childElement = child as HTMLElement;
          childElement.style.setProperty('pointer-events', 'auto', 'important');
          
          // Se for um input, também aplica cor preta
          if (childElement.tagName === 'INPUT' && 
              ((childElement as HTMLInputElement).type === 'text' || 
               (childElement as HTMLInputElement).type === 'tel' ||
               childElement.id?.toLowerCase().includes('phone'))) {
            childElement.style.setProperty('color', '#000000', 'important');
            childElement.style.setProperty('-webkit-text-fill-color', '#000000', 'important');
          }
        });
        
        // Força o elemento a ser visível removendo qualquer estilo inline que o esconda
        const computedStyle = window.getComputedStyle(htmlElement);
        console.log(`🔍 [showWidget] Estilo computado do elemento ${index + 1}:`, {
          display: computedStyle.display,
          visibility: computedStyle.visibility,
          opacity: computedStyle.opacity,
          width: computedStyle.width,
          height: computedStyle.height,
          position: computedStyle.position
        });
        
        if (computedStyle.display === 'none') {
          htmlElement.style.setProperty('display', 'block', 'important');
          console.log(`✅ [showWidget] Forçou display: block no elemento ${index + 1}`);
        }
        if (computedStyle.visibility === 'hidden') {
          htmlElement.style.setProperty('visibility', 'visible', 'important');
          console.log(`✅ [showWidget] Forçou visibility: visible no elemento ${index + 1}`);
        }
        if (computedStyle.opacity === '0') {
          htmlElement.style.setProperty('opacity', '1', 'important');
          console.log(`✅ [showWidget] Forçou opacity: 1 no elemento ${index + 1}`);
        }
        
        // Se o elemento tem width ou height zero, tenta definir um tamanho mínimo
        if (computedStyle.width === '0px' || computedStyle.height === '0px') {
          htmlElement.style.setProperty('min-width', '200px', 'important');
          htmlElement.style.setProperty('min-height', '200px', 'important');
          console.log(`✅ [showWidget] Definiu tamanho mínimo no elemento ${index + 1}`);
        }
        
        // Tenta focar no elemento se for um iframe
        if (htmlElement.tagName === 'IFRAME') {
          try {
            (htmlElement as HTMLIFrameElement).focus();
            console.log(`✅ [showWidget] Focou no iframe ${index + 1}`);
          } catch (error) {
            console.warn(`⚠️ [showWidget] Erro ao focar iframe ${index + 1}:`, error);
          }
        }
      }
    });
    
    console.log('✅ [showWidget] Widget Sonax deve estar visível agora');
  }, []);

  // Função combinada: abre widget e faz chamada
  const openWidgetAndCall = useCallback(
    async (phoneNumber: string, options?: { activityId?: string; dealId?: string }): Promise<void> => {
    // Verifica se está em uma página permitida
    if (!isAllowedPage()) {
      console.warn('Widget Sonax não pode ser usado nesta página:', window.location.pathname);
      return;
    }

    // Verifica se o usuário está autenticado antes de tentar usar o widget
    if (!user || userLoading) {
      console.warn('Usuário não autenticado - não é possível usar widget Sonax');
      return;
    }

    if (!hasScript) {
      console.warn('Script do Sonax não configurado');
      return;
    }

    // Evita execuções múltiplas simultâneas
    if (processingRef.current) {
      return;
    }

    processingRef.current = true;
    setIsProcessing(true);

    pendingCrmActivityIdRef.current = options?.activityId ?? null;

    // Arma o gate (carrega o script sob demanda) — ação explícita de ligar.
    armSonax();

    // Posiciona o widget IMEDIATAMENTE — centralizado aqui para todos os call sites
    (window as any).__sonaxWidgetOpen = true;
    expandWidget();

    // Pré-configura o callDataRef com o telefone que será discado (identifica a ligação no fim)
    const now = new Date();
    callDataRef.current = {
      data_inicio: now.toISOString().split('T')[0],
      hora_inicio: now.toTimeString().split(' ')[0].substring(0, 5),
      telefone: phoneNumber.replace(/\D/g, ''),
      // Preferimos o deal_id explícito do call site (ex.: DealProfileV2 / lista de deals);
      // a regex da URL fica só como fallback para call sites que ainda não o passam.
      deal_id: options?.dealId || window.location.pathname.match(/\/deals\/([a-f0-9-]+)/)?.[1] || undefined,
    };
    console.log(`📞 [openWidgetAndCall] callDataRef pré-configurado com telefone: ${phoneNumber}`);

    try {
      // Carrega o script se ainda não foi carregado
      if (!scriptLoadedRef.current) {
        await loadSonaxScript();
        // Aguarda o script carregar completamente
        await new Promise(resolve => setTimeout(resolve, 3000));
      }

      // Aguarda o widget estar disponível no DOM
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Garante visibilidade do widget via flag global (lido pelo SonaxHeaderButton)
      (window as any).__sonaxWidgetOpen = true;
      expandWidget();
      window.dispatchEvent(new CustomEvent('sonax-request-show-widget'));

      // Abre painéis internos do widget e preenche o número
      await openWidgetViaDOM(phoneNumber);
    } catch (error) {
      console.error('Erro ao abrir widget e fazer chamada:', error);
      throw error;
    } finally {
      // Libera o processamento após um delay para evitar loops
      setTimeout(() => {
        processingRef.current = false;
        setIsProcessing(false);
      }, 3000);
    }
  },
  [isAllowedPage, user, userLoading, hasScript, populateDialerField, clickCallButton, loadSonaxScript, showWidget, openWidgetViaDOM]);

  const hideWidget = useCallback(() => {
    const sonaxElements = document.querySelectorAll('[id*="sonax"], [class*="sonax"], [id*="webphone"], [class*="webphone"]');
    sonaxElements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      if (htmlElement) {
        // Ignora os campos de config Sonax e o botão de softphone do header (componente do CRM).
        if (htmlElement.id === 'sonax_widget_script' ||
            htmlElement.id?.includes('sonax_widget_script') ||
            htmlElement.id === 'id_ligacao' ||
            htmlElement.id === 'senha_ligacao' ||
            htmlElement.classList.contains('sonax-header-btn')) {
          return;
        }
        htmlElement.style.display = 'none';
        htmlElement.style.setProperty('pointer-events', 'none', 'important');
        htmlElement.removeAttribute('data-sonax-visible');

        // Remove estilos de posicionamento que podem criar overlay invisível
        htmlElement.style.removeProperty('position');
        htmlElement.style.removeProperty('left');
        htmlElement.style.removeProperty('right');
        htmlElement.style.removeProperty('top');
        htmlElement.style.removeProperty('bottom');
        htmlElement.style.removeProperty('transform');
        htmlElement.style.removeProperty('z-index');
        
        // Desabilita pointer-events em TODOS os elementos filhos e descendentes
        const allChildren = htmlElement.querySelectorAll('*');
        allChildren.forEach((child) => {
          const childElement = child as HTMLElement;
          if (childElement) {
            childElement.style.setProperty('pointer-events', 'none', 'important');
          }
        });
      }
    });
  }, []);

  // Apenas abre o widget (sem discar)
  // Uso: botao header que so quer exibir o softphone
  const openWidgetOnly = useCallback(async (): Promise<void> => {
    if (!isAllowedPage()) return;
    if (!user || userLoading || !hasScript) return;

    // Abrir o softphone do header = ação explícita → arma o gate (carrega/registra).
    armSonax();

    (window as any).__sonaxWidgetOpen = true;
    expandWidget();

    // Se o script nao foi carregado ainda, carrega e espera o DOM
    if (!scriptLoadedRef.current) {
      await loadSonaxScript();
      // Espera o script externo criar o DOM
      await new Promise(resolve => setTimeout(resolve, 3000));
    }

    // Se o DOM ainda nao existe, espera mais um pouco
    if (!getOuterContainer()) {
      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    // Expande o widget
    expandWidget();
    setTimeout(() => expandWidget(), 500);
  }, [isAllowedPage, user, userLoading, hasScript, loadSonaxScript]);

  /**
   * Sets the activity id that the next finished call completes (evento
   * `mengage:complete-activity-from-call`). Use when initiating the call
   * directly from the Sonax widget UI (not via openWidgetAndCall). O vínculo
   * ligação↔atividade no banco vem de call_dial_intents + sonax-webhook.
   * Call with `null` to clear (e.g. when leaving focus mode or selecting
   * a non-call activity).
   */
  const setActivityId = useCallback((id: string | null) => {
    pendingCrmActivityIdRef.current = id;
  }, []);

  return {
    makeCall,
    openWidgetAndCall,
    openWidgetOnly,
    populateDialerField,
    setActivityId,
    cleanupSonaxScripts,
    isActive,
    isLoaded,
    isLoading,
    isProcessing,
    hasScript,
    showWidget,
    hideWidget
  };
};